/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { AlertDialog as BaseAlertDialog } from "@base-ui-components/react/alert-dialog";
import { Dialog as BaseDialog } from "@base-ui-components/react/dialog";
import { useTheme } from "@bms/common";
import classNames from "classnames";
import Color from "color";
import * as React from "react";

import { IMediaButtonProps, MediaButton, useBreakpoints } from "components";
import CloseOutlined from "resources/icons/x.svg";

import "./Dialog.scss";

type getContainerFunc = () => HTMLElement;
type BaseDialogParts = typeof BaseDialog | typeof BaseAlertDialog;
type OpenChangeDetails =
  | Parameters<
      NonNullable<React.ComponentProps<typeof BaseDialog.Root>["onOpenChange"]>
    >[1]
  | Parameters<
      NonNullable<
        React.ComponentProps<typeof BaseAlertDialog.Root>["onOpenChange"]
      >
    >[1];

export interface IDialogProps {
  visible?: boolean;
  confirmLoading?: boolean;
  title?: React.ReactNode | string;
  titleHidden?: boolean;
  description?: React.ReactNode | string;
  descriptionHidden?: boolean;
  closable?: boolean;
  onOk?: (e: React.MouseEvent<HTMLElement>) => void;
  onCancel?: (e: Event) => void;
  afterClose?: () => void;
  centered?: boolean;
  width?: number;
  footer?: React.ReactNode;
  okText?: React.ReactNode;
  cancelText?: React.ReactNode;
  maskClosable?: boolean;
  forceRender?: boolean;
  okButtonProps?: IMediaButtonProps;
  cancelButtonProps?: IMediaButtonProps;
  destroyOnClose?: boolean;
  style?: React.CSSProperties;
  wrapClassName?: string;
  maskTransitionName?: string;
  transitionName?: string;
  className?: string;
  getContainer?: string | HTMLElement | getContainerFunc | false | null;
  zIndex?: number;
  bodyStyle?: React.CSSProperties;
  maskStyle?: React.CSSProperties;
  mask?: boolean;
  keyboard?: boolean;
  prefixCls?: string;
  closeIcon?: React.ReactNode;
  children?: React.ReactNode;
}

type IDialogInterface = React.FC<IDialogProps>;
const DIALOG_MANAGED_INERT_ATTR = "data-dialog-managed-inert";
const DIALOG_FOCUS_GUARD_ATTR = "data-dialog-focus-guard";
const DIALOG_FOCUSABLE_SELECTOR =
  'a[href], button, input:not([type="hidden"]):not([tabindex="-1"]), select:not([tabindex="-1"]), textarea:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]), [role="button"]:not([tabindex="-1"]), [role="combobox"]:not([tabindex="-1"]), [role="menuitem"]:not([tabindex="-1"]), [role="radio"]:not([tabindex="-1"])';
let activeDialogA11yLocks = 0;
let dialogA11yObserver: MutationObserver | null = null;

const syncDialogModalA11y = () => {
  if (typeof document === "undefined") {
    return;
  }

  document
    .querySelectorAll<HTMLElement>('[data-base-ui-inert][aria-hidden="true"]')
    .forEach((element) => {
      const containsFocusable =
        element.matches(DIALOG_FOCUSABLE_SELECTOR) ||
        Boolean(element.querySelector(DIALOG_FOCUSABLE_SELECTOR));

      if (!containsFocusable) {
        return;
      }

      if (!element.hasAttribute("inert")) {
        element.setAttribute("inert", "");
        element.setAttribute(DIALOG_MANAGED_INERT_ATTR, "");
      }
    });

  document
    .querySelectorAll<HTMLElement>(
      '[data-base-ui-focus-guard][aria-hidden="true"]',
    )
    .forEach((element) => {
      element.removeAttribute("aria-hidden");
      element.setAttribute(DIALOG_FOCUS_GUARD_ATTR, "");
    });
};

const cleanupDialogModalA11y = () => {
  if (typeof document === "undefined" || activeDialogA11yLocks > 0) {
    return;
  }

  document
    .querySelectorAll<HTMLElement>(`[${DIALOG_FOCUS_GUARD_ATTR}]`)
    .forEach((element) => {
      element.setAttribute("aria-hidden", "true");
      element.removeAttribute(DIALOG_FOCUS_GUARD_ATTR);
    });

  document
    .querySelectorAll<HTMLElement>(`[${DIALOG_MANAGED_INERT_ATTR}]`)
    .forEach((element) => {
      if (!element.hasAttribute("data-base-ui-inert")) {
        element.removeAttribute("inert");
        element.removeAttribute(DIALOG_MANAGED_INERT_ATTR);
      }
    });
};

const retainDialogModalA11y = () => {
  if (typeof document === "undefined") {
    return () => undefined;
  }

  activeDialogA11yLocks += 1;

  if (!dialogA11yObserver) {
    dialogA11yObserver = new MutationObserver(syncDialogModalA11y);
    dialogA11yObserver.observe(document.body, {
      attributes: true,
      attributeFilter: [
        "aria-hidden",
        "data-base-ui-focus-guard",
        "data-base-ui-inert",
      ],
      childList: true,
      subtree: true,
    });
  }

  syncDialogModalA11y();

  return () => {
    activeDialogA11yLocks = Math.max(0, activeDialogA11yLocks - 1);

    if (activeDialogA11yLocks === 0) {
      dialogA11yObserver?.disconnect();
      dialogA11yObserver = null;
      queueMicrotask(cleanupDialogModalA11y);
      window.setTimeout(cleanupDialogModalA11y, 0);
      window.setTimeout(cleanupDialogModalA11y, 50);
    }
  };
};

const resolveContainer = (
  getContainer: IDialogProps["getContainer"],
): HTMLElement | null => {
  if (typeof document === "undefined") {
    return null;
  }

  if (getContainer === false) {
    return null;
  }

  if (!getContainer) {
    return document.body || document.getElementById("root");
  }

  if (typeof getContainer === "string") {
    return document.querySelector(getContainer);
  }

  if (typeof getContainer === "function") {
    return getContainer();
  }

  return getContainer;
};

const DialogBase: React.FC<IDialogProps & { alert?: boolean }> = (props) => {
  const { isXS, isSM } = useBreakpoints();
  const { themeProvider } = useTheme();

  const onCancelClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { onCancel } = props;
    if (onCancel) {
      onCancel(e.nativeEvent);
    }
  };

  const onOkClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { onOk } = props;
    if (onOk) {
      onOk(e);
    }
  };

  const renderFooter = () => {
    const { confirmLoading, okText, cancelText } = props;
    return (
      <>
        <MediaButton
          variant="plain"
          onClick={onCancelClick}
          {...props.cancelButtonProps}
        >
          {cancelText || "Cancel"}
        </MediaButton>
        <MediaButton
          variant="plain"
          onClick={onOkClick}
          loading={confirmLoading}
          {...props.okButtonProps}
        >
          {okText || "OK"}
        </MediaButton>
      </>
    );
  };

  const {
    afterClose,
    alert,
    bodyStyle,
    children,
    className,
    closable,
    closeIcon,
    description,
    descriptionHidden,
    destroyOnClose,
    footer,
    forceRender,
    getContainer,
    keyboard,
    mask = true,
    maskClosable,
    maskStyle,
    onCancel,
    style,
    title,
    titleHidden,
    visible = false,
    width,
    wrapClassName,
    zIndex,
  } = props;

  const defaultFooter = <>{renderFooter()}</>;

  const closeIconToRender = (
    <span aria-hidden="true" className="Dialog-close-x">
      {closeIcon || <CloseOutlined />}
    </span>
  );

  const Parts: BaseDialogParts = alert ? BaseAlertDialog : BaseDialog;
  const portalContainer = resolveContainer(getContainer);
  const keepMounted = Boolean(forceRender || !destroyOnClose);
  const wrapClassNameExtended = classNames(
    "Dialog-wrap",
    "Dialog-centered",
    wrapClassName,
    {
      "Dialog-wrap--hidden": !visible,
    },
  );
  const dialogStyle: React.CSSProperties = {
    backgroundColor: `${Color(
      themeProvider.getColor("AppModalsBackgroundColor").toString(),
    )}`,
    width: isXS || isSM ? "80%" : width ?? 520,
    ...style,
  };
  const layerStyle: React.CSSProperties | undefined = zIndex
    ? { zIndex }
    : undefined;

  React.useEffect(() => {
    if (!visible) {
      return undefined;
    }

    return retainDialogModalA11y();
  }, [visible]);

  const renderTitle = () => {
    if (!title) {
      return null;
    }

    if (titleHidden) {
      return <Parts.Title className="sr-only">{title}</Parts.Title>;
    }

    return (
      <div className="Dialog-header">
        <Parts.Title className="Dialog-title">{title}</Parts.Title>
      </div>
    );
  };

  const renderDescription = () => {
    if (!description) {
      return null;
    }

    return (
      <Parts.Description
        className={descriptionHidden ? "sr-only" : "Dialog-description"}
      >
        {description}
      </Parts.Description>
    );
  };

  const handleOpenChange = (open: boolean, eventDetails: OpenChangeDetails) => {
    if (!open && keyboard === false && eventDetails.reason === "escape-key") {
      eventDetails.cancel();
      return;
    }

    if (!open && onCancel) {
      onCancel(eventDetails.event);
    }
  };

  const content = (
    <>
      {mask && (
        <Parts.Backdrop
          className="Dialog-mask"
          style={{ ...layerStyle, ...maskStyle }}
        />
      )}
      <div className={wrapClassNameExtended} style={layerStyle}>
        <Parts.Popup
          className={classNames("Dialog", className)}
          initialFocus={true}
          finalFocus={true}
          style={dialogStyle}
        >
          <div className="Dialog-content">
            {closable !== false && (
              <Parts.Close className="Dialog-close" aria-label="Close dialog">
                {closeIconToRender}
              </Parts.Close>
            )}
            {renderTitle()}
            {renderDescription()}
            <div className="Dialog-body" style={bodyStyle}>
              {children}
            </div>
            {footer !== null && (
              <div className="Dialog-footer">
                {footer === undefined ? defaultFooter : footer}
              </div>
            )}
          </div>
        </Parts.Popup>
      </div>
    </>
  );

  const rootProps = {
    open: visible,
    onOpenChange: handleOpenChange,
    onOpenChangeComplete: (open: boolean) => {
      if (!open) {
        afterClose?.();
      }
    },
  };

  if (alert) {
    return (
      <BaseAlertDialog.Root {...rootProps}>
        {getContainer === false ? (
          content
        ) : (
          <BaseAlertDialog.Portal
            container={portalContainer}
            keepMounted={keepMounted}
          >
            {content}
          </BaseAlertDialog.Portal>
        )}
      </BaseAlertDialog.Root>
    );
  }

  return (
    <BaseDialog.Root
      {...rootProps}
      dismissible={maskClosable !== false}
      modal={true}
    >
      {getContainer === false ? (
        content
      ) : (
        <BaseDialog.Portal
          container={portalContainer}
          keepMounted={keepMounted}
        >
          {content}
        </BaseDialog.Portal>
      )}
    </BaseDialog.Root>
  );
};

const Dialog: IDialogInterface = (props) => <DialogBase {...props} />;

const AlertDialog: IDialogInterface = (props) => (
  <DialogBase {...props} alert={true} />
);

Dialog.defaultProps = {
  transitionName: "zoom",
  maskTransitionName: "fade",
  confirmLoading: false,
  visible: false,
};

AlertDialog.defaultProps = Dialog.defaultProps;

export { AlertDialog, Dialog };
